import React, { useCallback, useEffect } from 'react';

import qs from 'query-string';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
    POPUP_AXIS,
    Select,
    SELECT_THEMES,
    Tabs,
    useGeneratePath,
    useStaticUrls,
    Spin,
    Link,
} from 'platform-components';
import { useMedia } from 'react-use';
import { useIntl } from 'react-intl';

import { getCurrentClass } from 'common.components/user/selectors';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { Text } from '@edu-frontend/modules/src/components/ui/Text';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';

import { useHistory, useLocation, useParams } from 'react-router';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { CourseContainerModuleLessonsGroup } from '../../bundles/lab/components/library.index/__informatics/__modules/library__informatics__modules';
import { useCourseContainers } from '../../bundles/lab/components/library.course-container/with-course-container';

import { CourseContainerParagraphs } from '../../bundles/lab/components/library.index/__informatics/__course-container/library__informatics__course-container';
import { actions as courseContainerActions } from '../../bundles/lab/components/library.course-container/actions';
import { getOpenedCourseContainerSlug } from '../../bundles/lab/components/library.course-container/selectors';
import { useLastLessonWidgetState } from './useLastLessonWidgetState';

import './course-container-last-lesson-widget.scss';

interface CourseContainerLastLessonWidgetProps {
    className?: string;
    /** Заголовок виджета */
    header?: string;
    /** Предмет, информатика по умолчанию */
    subject?: string;
}

/** В виджете показывается последний в котором была выдача и следующий урок по всем выданным в класс курс-контейнерам */
// eslint-disable-next-line complexity
export const CourseContainerLastLessonWidget = ({
    className,
    header,
    subject = 'informatics',
}: CourseContainerLastLessonWidgetProps) => {
    const isMobile = useMedia('(max-width: 767px)');
    const dispatch = useDispatch();
    const intl = useIntl();
    const { labLibrarySubjectCollection } = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const location = useLocation();
    const history = useHistory();
    const { classId = null } = useParams<{ classId?: string }>();

    const queryParams = qs.parse(location.search);

    const currentTeam = useSelector((store) => getCurrentClass(store, { classId }));
    const openedCourseContainerSlug = useSelector(getOpenedCourseContainerSlug);

    const handleTabChangeCourseContainer = useCallback(
        (slug: string) => {
            dispatch(courseContainerActions.setOpenedCourseContainerSlug({ slug }));
        },
        [dispatch]
    );
    const handleSelectChangeCourseContainer = useCallback(
        ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
            dispatch(courseContainerActions.setOpenedCourseContainerSlug({ slug: target.value }));
        },
        [dispatch]
    );

    const courseId = useSelector((store) => getCurrentYearCourseId(store, { classId, subject })) ?? undefined;

    const { data: courseContainers = [] } = useCourseContainers(courseId);

    // если открытый контейнер отсутствует, записываем первый из списка выданных курс-контейнеров
    useEffect(() => {
        if (courseContainers) {
            const isOpenedContainerSlugNotInCourseContainers = !courseContainers.find(
                (container) => container.container.slug === openedCourseContainerSlug
            );

            // необходимо проверить на наличие контейнеров, если их нет получаем бесконечные рендеры
            if (isOpenedContainerSlugNotInCourseContainers && courseContainers.length) {
                const courseContainerSlug = courseContainers[0]?.container?.slug;
                dispatch(courseContainerActions.setOpenedCourseContainerSlug({ slug: courseContainerSlug }));
            }
        }
    }, [courseContainers, openedCourseContainerSlug, dispatch]);

    // После размонтирования компонента, удаляем openedCourseContainerSLug
    // т.к. он используется для привязки курс-контейнера при выдаче заданий
    useEffect(() => {
        return () => {
            dispatch(courseContainerActions.setOpenedCourseContainerSlug({ slug: null }));
        };
    }, [dispatch]);

    const selectedCourseContainer = courseContainers?.find(
        (courseContainer) => courseContainer.container.slug === openedCourseContainerSlug
    );
    const {
        isLoading,
        currentLessons,
        nextLessons,
        currentLessonGroupName,
        nextLessonGroupName,
        currentModuleId,
        nextModuleId,
    } = useLastLessonWidgetState(selectedCourseContainer, courseId);

    const onCurrentLessonsMenuClick = useCallback(() => {
        history.replace({ search: qs.stringify({ ...queryParams, module_id: currentModuleId }) });
    }, [currentModuleId, history, queryParams]);

    const onNextLessonsMenuClick = useCallback(() => {
        history.replace({ search: qs.stringify({ ...queryParams, module_id: nextModuleId }) });
    }, [nextModuleId, history, queryParams]);

    // если нет выданных курс-контейнеров или ни один не выбран
    if (!classId || !courseContainers || courseContainers.length === 0 || !openedCourseContainerSlug) {
        return null;
    }

    const innerHeader =
        header ??
        intl.formatMessage(
            {
                id: 'courseContainer.nextLessonsWidget.header',
            },
            { level: currentTeam?.level, name: currentTeam?.name }
        );

    const rubricatorUrl = getRouterPath(labLibrarySubjectCollection, {
        classId,
        subject: 'informatics',
        containerSlug: openedCourseContainerSlug,
    });

    return (
        <ThemeProvider isRoot={false} theme={hrLight}>
            <div className={cn('course-container-last-lesson-widget', className)}>
                {innerHeader && <Text typography="titleM">{innerHeader}</Text>}
                <div className="course-container-last-lesson-widget__description">
                    {isMobile ? (
                        <Select
                            onSelect={handleSelectChangeCourseContainer}
                            popupAxis={POPUP_AXIS.END}
                            theme={SELECT_THEMES.ROUNDED}
                            value={openedCourseContainerSlug}
                        >
                            {courseContainers.map(({ container: { slug } }) => (
                                <Select.Option key={slug} value={slug}>
                                    {intl.formatMessage({
                                        id: `courseModuleBanner.${slug}.header`,
                                    })}
                                </Select.Option>
                            ))}
                        </Select>
                    ) : (
                        <Tabs mix="course-container-last-lesson-widget__tabs" theme={Tabs.THEME.ROUNDED}>
                            {courseContainers.map(({ container: { slug } }) => {
                                const isSelected = slug === openedCourseContainerSlug;

                                const courseModuleName = intl.formatMessage({
                                    id: `courseModuleBanner.${slug}.header`,
                                });

                                return (
                                    <Tabs.Tab
                                        id={slug}
                                        key={slug}
                                        mix="test"
                                        onTabSelect={handleTabChangeCourseContainer}
                                        selected={isSelected}
                                    >
                                        <Text
                                            className="course-container-last-lesson-widget__cutText"
                                            title={courseModuleName}
                                        >
                                            {courseModuleName}
                                        </Text>
                                    </Tabs.Tab>
                                );
                            })}
                        </Tabs>
                    )}
                    <div>
                        <Text typography="bodyM">
                            {intl.formatMessage({
                                id: `courseModuleBanner.${openedCourseContainerSlug}.description`,
                            })}
                        </Text>
                        <Link
                            className="course-container-last-lesson-widget__rubricator-link"
                            isRouter
                            to={rubricatorUrl}
                        >
                            Оглавление программы
                        </Link>
                    </div>
                </div>
                <div className="course-container-last-lesson-widget__content">
                    {isLoading && <Spin />}
                    {!currentLessons && !nextLessons && !isLoading && (
                        <Text typography="bodyM">
                            {intl.formatMessage({
                                id: 'courseContainer.nextLessonsWidget.empty',
                            })}
                        </Text>
                    )}
                    {currentLessons && (
                        <CourseContainerModuleLessonsGroup
                            className="course-container-last-lesson-widget__lessons"
                            data-testid="current-lessons-group"
                            description={intl.formatMessage({
                                id: 'courseContainer.nextLessonsWidget.currentDescription',
                            })}
                            lessons={currentLessons}
                            name={currentLessonGroupName}
                            onMenuClick={onCurrentLessonsMenuClick}
                        />
                    )}
                    {nextLessons && (
                        <CourseContainerModuleLessonsGroup
                            className="course-container-last-lesson-widget__lessons"
                            data-testid="next-lessons-group"
                            description={intl.formatMessage({
                                id: 'courseContainer.nextLessonsWidget.nextDescription',
                            })}
                            lessons={nextLessons}
                            name={nextLessonGroupName}
                            onMenuClick={onNextLessonsMenuClick}
                        />
                    )}
                </div>
            </div>
            <CourseContainerParagraphs containerSlug={openedCourseContainerSlug} isOnlyDrawer={true} />
        </ThemeProvider>
    );
};
